import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import HeroIPhoneCluster from "@/components/HeroIPhoneCluster";
import StickyPhoneStory from "@/components/StickyPhoneStory";
import IPhoneMockup from "@/components/IPhoneMockup";
import AppStoreBadges from "@/components/AppStoreBadges";
import RestaurantOrigin from "@/components/RestaurantOrigin";
import WaitlistForm from "@/components/WaitlistForm";
import FAQAccordion from "@/components/FAQAccordion";
import PreferencesSection from "@/components/PreferencesSection";
import SavedShowcaseSection from "@/components/SavedShowcaseSection";
import PopularSearchesGrid from "@/components/PopularSearchesGrid";
import { useLanguage } from "@/context/LanguageContext";
import { useNotify } from "@/context/NotifyContext";
import { trackScan } from "@/lib/api";
import { track, trackOnce } from "@/lib/analytics";
import { APP_SCREENS, APP_SCREENS_ALT, LOGOS } from "@/lib/assets";

export default function HomeLanding() {
  const { t, lang } = useLanguage();
  const { openNotify } = useNotify();

  useEffect(() => {
    trackScan({ page: "home", language: lang });
    track("page_view", { referrer: document.referrer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => {
      const sc = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const pct = (sc / total) * 100;
      if (pct >= 25) trackOnce("sd25", "scroll_depth_25");
      if (pct >= 50) trackOnce("sd50", "scroll_depth_50");
      if (pct >= 75) trackOnce("sd75", "scroll_depth_75");
      if (pct >= 98) trackOnce("sd100", "scroll_depth_100");
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const reduced = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });
  const fadeUpView = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="home-landing" className="relative">
      <Nav />

      {/* HERO */}
      <section className="relative pt-24 lg:pt-28 pb-12 lg:pb-16" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-6 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.div {...fade(0)} className="section-label inline-flex items-center gap-2.5" data-testid="hero-label">
              <img src={LOGOS.star} alt="" className="w-6 h-6 select-none feaststar-logo" aria-hidden="true" draggable="false" />
              <span>{t("home.hero.label")}</span>
            </motion.div>
            <h1 className="display-xl">
              <motion.span className="block" {...fade(0.08)} data-testid="hero-h1-a">{t("home.hero.h1a")}</motion.span>
              <motion.span className="block italic text-accent" {...fade(0.16)} data-testid="hero-h1-b">
                {t("home.hero.h1b")}
              </motion.span>
            </h1>
            <motion.p {...fade(0.24)} className="body-lg mt-6 max-w-xl">{t("home.hero.sub")}</motion.p>

            <motion.div {...fade(0.32)} className="mt-8 flex flex-wrap gap-3">
              <a
                href="#download"
                data-testid="hero-cta-primary"
                onClick={(e) => {
                  e.preventDefault();
                  track("hero_cta_click", { button: "app_entdecken" });
                  openNotify("hero-cta-primary");
                }}
                className="btn-primary"
              >
                {t("home.hero.ctaPrimary")} <ArrowDownRight size={18} />
              </a>
              <a
                href="#restaurant-origin"
                data-testid="hero-cta-secondary"
                onClick={() => track("hero_cta_click", { button: "restaurant_auswaehlen" })}
                className="btn-ghost"
              >
                {t("home.hero.ctaSecondary")} <ArrowRight size={18} />
              </a>
            </motion.div>

            <motion.div {...fade(0.4)} className="mt-7 mb-2 lg:mb-0">
              <AppStoreBadges />
            </motion.div>
          </div>

          {/* iPhone cluster — single phone on mobile, 3 phones on desktop */}
          <div className="lg:col-span-6 relative mt-10 lg:mt-0 mb-12 lg:mb-0">
            <div className="relative w-full mx-auto" data-testid="hero-phone-canvas">
              <HeroIPhoneCluster reducedMotion={reduced} />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section-pad-sm" data-testid="problem-section">
        <div className="max-w-[760px] mx-auto px-5 lg:px-12 text-center">
          <motion.div {...fadeUpView()} className="section-label">{t("home.problem.label")}</motion.div>
          <motion.h2 {...fadeUpView(0.05)} className="display-md">
            <span className="block">{t("home.problem.h2a")}</span>
            <span className="block italic text-accent">{t("home.problem.h2b")}</span>
          </motion.h2>
          <motion.p {...fadeUpView(0.1)} className="body-lg mt-8 whitespace-pre-line text-left">
            {t("home.problem.body")}
          </motion.p>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="section-pad-sm" data-testid="solution-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <div className="text-center max-w-[820px] mx-auto mb-14">
            <motion.div {...fadeUpView()} className="section-label">{t("home.solution.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("home.solution.h2a")}</span>
              <span className="block italic text-accent">{t("home.solution.h2b")}</span>
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="solution-cards">
            {t("home.solution.cards").map((c, i) => (
              <motion.div key={i} {...fadeUpView(i * 0.06)} className="card-standard">
                <div className="font-mono text-[12px] text-accent mb-3">0{i + 1}</div>
                <div className="display-sm mb-3">{c.title}</div>
                <div className="body-md">{c.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — STICKY STORY */}
      <section id="how-it-works" className="section-pad" data-testid="how-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <div className="max-w-[820px] mb-16">
            <motion.div {...fadeUpView()} className="section-label">{t("home.howItWorks.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("home.howItWorks.h2a")}</span>
              <span className="block italic text-accent">{t("home.howItWorks.h2b")}</span>
            </motion.h2>
          </div>
          <StickyPhoneStory />
        </div>
      </section>

      {/* CRITERIA — 4 GROUPS + PERSONA PRESETS */}
      <PreferencesSection
        groups={t("home.criteria.groups")}
        label={t("home.criteria.label")}
        h2a={t("home.criteria.h2a")}
        h2b={t("home.criteria.h2b")}
        sub={t("home.criteria.sub")}
      />

      {/* QR-ONBOARDING */}
      <section id="restaurant-origin" className="section-pad-sm" data-testid="origin-section">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <motion.div {...fadeUpView()} className="section-label">{t("home.origin.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("home.origin.h2a")}</span>
              <span className="block italic text-accent">{t("home.origin.h2b")}</span>
            </motion.h2>
            <motion.p {...fadeUpView(0.1)} className="body-lg mt-5">{t("home.origin.sub")}</motion.p>
          </div>
          <div className="lg:col-span-7">
            <RestaurantOrigin />
          </div>
        </div>
      </section>

      {/* DOWNLOAD */}
      <section id="download" className="section-pad-sm" data-testid="download-section">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-12 text-center">
          <motion.div {...fadeUpView()} className="section-label">{t("home.download.label")}</motion.div>
          <motion.h2 {...fadeUpView(0.05)} className="display-lg">
            <span className="block">{t("home.download.h1a")}</span>
            <span className="block italic text-accent">{t("home.download.h1b")}</span>
          </motion.h2>
          <motion.p {...fadeUpView(0.1)} className="body-lg mt-5 max-w-[640px] mx-auto">
            {t("home.download.sub")}
          </motion.p>

          <motion.div {...fadeUpView(0.15)} className="flex justify-center mt-12 mb-12">
            <IPhoneMockup
              width={290}
              image={APP_SCREENS.map}
              alt={APP_SCREENS_ALT.map}
              testId="download-iphone"
              priority
            />
          </motion.div>

          <div className="flex justify-center" onMouseEnter={() => trackOnce("dlview", "download_section_viewed")}>
            <AppStoreBadges />
          </div>

          <div className="mt-14 max-w-[520px] mx-auto">
            <div className="body-sm mb-4">{t("home.download.waitlistHint")}</div>
            <WaitlistForm />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="section-pad-sm" data-testid="proof-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <div className="text-center max-w-[820px] mx-auto mb-14">
            <motion.div {...fadeUpView()} className="section-label">{t("home.proof.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("home.proof.h2a")}</span>
              <span className="block italic text-accent">{t("home.proof.h2b")}</span>
            </motion.h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t("home.proof.cards").map((c, i) => (
              <motion.div key={i} {...fadeUpView(i * 0.05)} className="card-standard">
                <div className="display-sm mb-3">{c.title}</div>
                <div className="body-md">{c.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER TEASER */}
      <section className="section-pad-sm" data-testid="partner-teaser-section">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-12">
          <div className="card-premium">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="section-label">{t("home.partnerTeaser.label")}</div>
                <h2 className="display-md">
                  <span className="block">{t("home.partnerTeaser.h2a")}</span>
                  <span className="block italic text-accent">{t("home.partnerTeaser.h2b")}</span>
                </h2>
                <p className="body-lg mt-5 max-w-[560px]">{t("home.partnerTeaser.body")}</p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <Link to="/partner" data-testid="partner-teaser-primary" className="btn-olive justify-center">
                  {t("home.partnerTeaser.ctaPrimary")} <ArrowRight size={16} />
                </Link>
                <Link to="/partner" data-testid="partner-teaser-secondary" className="btn-ghost justify-center">
                  {t("home.partnerTeaser.ctaSecondary")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad-sm" data-testid="faq-section">
        <div className="max-w-[900px] mx-auto px-5 lg:px-12">
          <div className="mb-8">
            <div className="section-label">{t("home.faq.label")}</div>
            <h2 className="display-md">{t("home.faq.h2")}</h2>
          </div>
          <FAQAccordion items={t("home.faq.items")} />
        </div>
      </section>

      {/* SAVED — bottom showcase */}
      <SavedShowcaseSection t={t} />

      {/* POPULAR CITY × STYLE SEARCHES — internal linking */}
      <PopularSearchesGrid
        mode="mixed"
        lang={lang}
        headingDe="Beliebte Suchen"
        headingEn="Popular searches"
        subDe="Finde Restaurants, Cafés und Bäckereien, die zu dir passen — in deiner Stadt, nach deinem Ernährungsstil."
        subEn="Find restaurants, cafés and bakeries that fit you — in your city, by your diet style."
        limit={24}
      />

      <Footer variant="home" />
    </div>
  );
}
